// Game Constants
const CANVAS_WIDTH = 300;
const CANVAS_HEIGHT = 400;
const BIRD_WIDTH = 34;
const BIRD_HEIGHT = 24;
const BIRD_X = 50;
const GRAVITY = 0.12;
const FLAP_STRENGTH = -4;
const PIPE_WIDTH = 52;
const PIPE_GAP = 140; // Vertical gap between pipes
const PIPE_SPEED = 2;
const PIPE_SPAWN_RATE = 150; // Frames between pipe spawns

export function initGame(canvas) {
  if (!canvas) {
    console.error("Canvas element not provided to initGame");
    return () => {}; // Return empty cleanup function
  }
  const ctx = canvas.getContext("2d");
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;

  let birdY = CANVAS_HEIGHT / 2 - BIRD_HEIGHT / 2;
  let birdVelocity = 0;
  let pipes = [];
  let score = 0;
  let gameOver = false;
  let gameStarted = false;
  let frameCount = 0;
  let gameLoopId = null;

  // --- Drawing Functions ---
  const drawBird = () => {
    // ctx.fillStyle = "yellow"; // No longer needed for emoji
    // ctx.fillRect(BIRD_X, birdY, BIRD_WIDTH, BIRD_HEIGHT); // Remove rectangle drawing
    ctx.font = `${BIRD_HEIGHT}px Arial`; // Set font size based on desired height
    ctx.fillText("💪", BIRD_X, birdY + BIRD_HEIGHT); // Draw emoji, adjust Y for baseline
  };

  const drawPipes = () => {
    ctx.fillStyle = "green";
    pipes.forEach((pipe) => {
      ctx.fillRect(pipe.x, 0, PIPE_WIDTH, pipe.topHeight);
      const bottomPipeY = pipe.topHeight + PIPE_GAP;
      ctx.fillRect(
        pipe.x,
        bottomPipeY,
        PIPE_WIDTH,
        CANVAS_HEIGHT - bottomPipeY
      );
    });
  };

  const drawScore = () => {
    ctx.fillStyle = "black";
    ctx.font = "24px Arial";
    ctx.fillText(`Score: ${score}`, 10, 30);
  };

  const drawGameOver = () => {
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.fillStyle = "white";
    ctx.font = "30px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Game Over", CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 20);
    ctx.font = "20px Arial";
    ctx.fillText(`Score: ${score}`, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 10);
    ctx.fillText(
      "Click or Space to Restart",
      CANVAS_WIDTH / 2,
      CANVAS_HEIGHT / 2 + 40
    );
    ctx.textAlign = "start";
  };

  const drawStartScreen = () => {
    ctx.fillStyle = "skyblue"; // Background for start screen
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.fillStyle = "black";
    ctx.font = "24px Arial";
    ctx.textAlign = "center";
    ctx.fillText(
      "Click or Space to Start",
      CANVAS_WIDTH / 2,
      CANVAS_HEIGHT / 2
    );
    ctx.textAlign = "start";
  };

  // --- Game Logic Functions ---
  const resetGame = () => {
    birdY = CANVAS_HEIGHT / 2 - BIRD_HEIGHT / 2;
    birdVelocity = 0;
    pipes = [];
    score = 0;
    gameOver = false;
    gameStarted = false;
    frameCount = 0;
    // Clear any existing loop before potentially starting a new one
    if (gameLoopId) {
      cancelAnimationFrame(gameLoopId);
      gameLoopId = null;
    }
    drawStartScreen(); // Draw the initial start screen
  };

  const flap = () => {
    if (!gameStarted) {
      gameStarted = true;
      birdVelocity = FLAP_STRENGTH;
      if (!gameLoopId) {
        // Start the loop only when the game actually starts
        gameLoopId = requestAnimationFrame(gameLoop);
      }
    } else if (!gameOver) {
      birdVelocity = FLAP_STRENGTH;
    }
  };

  const handleInteraction = () => {
    if (gameOver) {
      resetGame();
      // We call flap() here to immediately start the game on restart click/press
      // flap() will set gameStarted = true and start the loop
      flap();
    } else {
      flap(); // Handles both starting and in-game flapping
    }
  };

  // --- Game Loop ---
  const gameLoop = () => {
    if (!gameStarted || gameOver) {
      // Should not normally reach here if loop is managed correctly,
      // but as a safeguard, ensure loop stops.
      if (gameLoopId) cancelAnimationFrame(gameLoopId);
      gameLoopId = null;
      if (gameOver) drawGameOver(); // Ensure final screen is drawn if loop stops due to game over
      return;
    }

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.fillStyle = "skyblue";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Update bird
    birdVelocity += GRAVITY;
    birdY += birdVelocity;

    // Update pipes
    frameCount++;
    let passedPipeFlag = false;
    pipes = pipes
      .map((pipe) => ({ ...pipe, x: pipe.x - PIPE_SPEED }))
      .filter((pipe) => {
        if (!pipe.passed && pipe.x + PIPE_WIDTH < BIRD_X) {
          pipe.passed = true;
          passedPipeFlag = true;
        }
        return pipe.x + PIPE_WIDTH > 0;
      });

    if (passedPipeFlag) {
      score++;
    }

    // Spawn new pipes
    if (frameCount % PIPE_SPAWN_RATE === 0) {
      const minTopHeight = 50;
      const maxTopHeight = CANVAS_HEIGHT - PIPE_GAP - 50;
      const topHeight =
        Math.floor(Math.random() * (maxTopHeight - minTopHeight + 1)) +
        minTopHeight;
      pipes.push({ x: CANVAS_WIDTH, topHeight: topHeight, passed: false });
    }

    // Collision Detection
    // Ground/Ceiling
    if (birdY < 0 || birdY + BIRD_HEIGHT > CANVAS_HEIGHT) {
      gameOver = true;
    }
    // Pipes
    pipes.forEach((pipe) => {
      const birdCollidesX =
        BIRD_X < pipe.x + PIPE_WIDTH && BIRD_X + BIRD_WIDTH > pipe.x;
      const birdCollidesY =
        birdY < pipe.topHeight ||
        birdY + BIRD_HEIGHT > pipe.topHeight + PIPE_GAP;
      if (birdCollidesX && birdCollidesY) {
        gameOver = true;
      }
    });

    // Draw elements
    drawPipes();
    drawBird();
    drawScore();

    // Check game over state *after* drawing the current frame
    if (gameOver) {
      drawGameOver(); // Draw the game over screen on top
      cancelAnimationFrame(gameLoopId);
      gameLoopId = null;
    } else {
      // Continue loop
      gameLoopId = requestAnimationFrame(gameLoop);
    }
  };

  // --- Event Listeners ---
  const handleCanvasClick = (event) => {
    event.preventDefault(); // Keep for desktop clicks
    handleInteraction();
  };

  const handleTouchStart = (event) => {
    event.preventDefault(); // Prevent double-tap zoom, scrolling etc.
    handleInteraction();
  };

  const handleKeyDown = (event) => {
    if (event.code === "Space") {
      event.preventDefault();
      handleInteraction();
    }
  };

  // Add Listeners
  canvas.addEventListener("click", handleCanvasClick);
  canvas.addEventListener("touchstart", handleTouchStart); // Add touch listener
  window.addEventListener("keydown", handleKeyDown);

  // Initial Setup
  resetGame(); // Sets initial state and draws start screen

  // Cleanup function to be called when the component unmounts
  const cleanup = () => {
    if (gameLoopId) {
      cancelAnimationFrame(gameLoopId);
    }
    canvas.removeEventListener("click", handleCanvasClick);
    canvas.removeEventListener("touchstart", handleTouchStart); // Remove touch listener
    window.removeEventListener("keydown", handleKeyDown);
  };

  return cleanup;
}
